import React from "react";

import { ZodProvider } from "@autoform/zod";
import { useNavigation, useParsed } from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useCostSubscriptions } from "@/pages/tina/cost-subscriptions/hooks/use-cost-subscriptions";
import {
  type CostSubscriptionFormType,
  costSubscriptionFormSchema,
} from "@/pages/tina/cost-subscriptions/schemas/cost-subscription.form";

const RESOURCE = "tina-cost-subscriptions";
const schemaProvider = new ZodProvider(costSubscriptionFormSchema);

export const CostSubscriptionsEdit = () => {
  const { id } = useParsed();
  const { list } = useNavigation();
  const { subscriptions, isLoading, isError, create, update } =
    useCostSubscriptions();
  const setTitle = useDocumentTitle();

  // The API has no per-id GET, so the row comes out of the list.
  const existing = id
    ? subscriptions.find((row) => row.id === id.toString())
    : undefined;

  const values = React.useMemo<CostSubscriptionFormType | undefined>(
    () =>
      existing && {
        id: existing.id,
        name: existing.name,
        vendor: existing.vendor,
        monthly_usd: Number(existing.monthly_usd),
        starts_on: existing.starts_on,
        ends_on: existing.ends_on ?? "",
        note: existing.note ?? "",
      },
    [existing],
  );

  React.useEffect(() => {
    setTitle(existing ? `Edit ${existing.name}` : "Add subscription");
  }, [existing, setTitle]);

  const onSubmit = async (submitted: CostSubscriptionFormType) => {
    const ok = id
      ? await update(id.toString(), submitted)
      : await create(submitted);
    if (ok) list(RESOURCE);
  };

  if (id && !existing) {
    return (
      <div className="p-8 text-sm text-muted-foreground">
        {isLoading
          ? "Loading..."
          : isError
            ? "Could not load the subscriptions."
            : "Subscription not found."}
      </div>
    );
  }

  return (
    <div className="flex flex-row">
      <div className="w-3/4 p-8 flex flex-col space-y-4">
        <AutoForm
          schema={schemaProvider}
          defaultValues={values}
          onSubmit={onSubmit}
          {...injectCustomAutoFormComponents()}
        >
          <BottomActionBar className="justify-between">
            <Button type="submit">Submit</Button>
            {id && (
              <DeleteButton
                resource={RESOURCE}
                id={id.toString()}
                onSuccess={() => list(RESOURCE)}
              />
            )}
          </BottomActionBar>
        </AutoForm>
      </div>
    </div>
  );
};
